"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { CTAButton } from "@/shadcn/ui/button";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { PageHomePionexSchema } from "@/sanity/pages/PageHomePionexTemplate";

import cardBg from "@/public/lp2/green-banner/card-bg-pionex.png";
import pionexLogoImg from "@/public/lp2/global/logo-pionex.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function GreenBannerSection({
  data,
}: {
  data: PageHomePionexSchema;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const contentRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const lastTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      lastTL
        .from(containerRef.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(contentRef.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto lg:p-[20rem] pt-[12rem] pb-[8rem] !px-[2rem] lg:!px-[4rem]">
        <div className="green-card-section !h-full rounded-[3rem] flex flex-col items-center justify-center w-full py-[10rem] lg:py-[15rem] px-[2rem] lg:px-[13rem] relative">
          {/* Backgrounds */}
          <div className="green-card-section-blur-pionex absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
          <Image
            className="absolute !w-full !h-full object-top object-cover pointer-events-none rounded-[3rem]"
            alt="Card background"
            quality={100}
            src={cardBg}
          />
          {/* End backgrounds */}

          <div
            ref={contentRef}
            className="w-full flex flex-col items-center justify-center relative"
          >
            <Image
              className="w-[36rem] lg:w-[54.625rem] mb-[2rem]"
              alt="Logo"
              src={pionexLogoImg}
            />

            {data?.greenTexts && (
              <PortableText
                value={data?.greenTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h2: ({ children }) => (
                      <h2 className="gradient-title !text-white/80 text-[4rem] lg:text-[7rem] font-[600] tracking-[-0.16rem] lg:tracking-[-0.28rem] leading-[120%] text-center mt-[3rem] lg:px-[10rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-white text-center text-[2.25rem] font-[400] leading-[3.375rem] opacity-80 mb-[3rem] lg:w-[55%]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}

            <div className="mt-[1rem]">
              {data?.greenCTA && <CTAButton data={data?.greenCTA} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
